import produce from 'immer';
import { useCallback, useMemo } from 'react';

import { SectionAccordion } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/SectionAccordion';
import { LinkedStyleInputs } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/inputs';
import { SectionInputWrapper } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/sections/shared';
import { StyleValueBoxDimensionsConfig } from 'lib/inquiryStyles';
import {
  SpacingComponent,
  SpacingComponentConfig,
  SpacingKeys,
} from 'lib/interfaces/ui-components';

type SpacingDefaults = {
  [key: string]: { values: StyleValueBoxDimensionsConfig; label: string };
};

const DEFAULT_SIZINGS = {
  margin: {
    label: 'Margin',
  },
  padding: {
    label: 'Padding',
  },
};

type SpacingStylesProps = {
  componentPreview: SpacingComponent;
  component: SpacingComponentConfig;
  baseComponentConfig: SpacingComponentConfig;
  styleVariantIndex: number;
  editable: boolean;
  submitStyle: (field: string, values: any) => void;
};

const SpacingStyles = ({
  componentPreview,
  component,
  editable,
  styleVariantIndex,
  baseComponentConfig,
  submitStyle,
}: SpacingStylesProps) => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const renderable = Object.keys(baseComponentConfig['style-variants'][0]).filter(
    (key) => key in DEFAULT_SIZINGS,
  );

  const spacingDefaults: SpacingDefaults = useMemo(() => {
    const writable: SpacingDefaults = {};
    renderable.forEach((key: SpacingKeys) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      writable[key] = {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ...DEFAULT_SIZINGS[key],
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        values: componentPreview.styles?.[key]?.base?.base ?? {
          top: { value: 0, unit: 'px' },
          bottom: { value: 0, unit: 'px' },
          left: { value: 0, unit: 'px' },
          right: { value: 0, unit: 'px' },
        },
      };
    });
    return writable;
  }, [componentPreview.styles, renderable]);

  const submitSingle = useCallback(
    (value: string, field: string, root: string) => {
      const savedValue = parseInt(value);
      const newValues = produce(spacingDefaults, (newSpacingValues) => {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        newSpacingValues[root].values[field as keyof StyleValueBoxDimensionsConfig].value =
          savedValue;
        return newSpacingValues;
      });

      submitStyle(root, newValues[root].values);
    },
    [spacingDefaults, submitStyle],
  );

  const submitLinked = useCallback(
    (value: string, root: string) => {
      const savedValue = parseInt(value);

      submitStyle(root, {
        top: { value: savedValue, unit: 'px' },
        bottom: { value: savedValue, unit: 'px' },
        left: { value: savedValue, unit: 'px' },
        right: { value: savedValue, unit: 'px' },
      });
    },
    [submitStyle],
  );

  return (
    <SectionAccordion title='Spacing'>
      {Object.entries(spacingDefaults).map(([rootStyle, { label, values }]) => (
        <SectionInputWrapper key={`spacing-styles-root-${rootStyle}`}>
          <LinkedStyleInputs
            label={label}
            allowNegativeNumbers={rootStyle === 'margin'}
            defaultValues={values}
            rootStyle={rootStyle}
            submitSingle={submitSingle}
            submitLinked={submitLinked}
            id={`${component.name}-${styleVariantIndex}`}
            editable={editable}
          />
        </SectionInputWrapper>
      ))}
    </SectionAccordion>
  );
};

export default SpacingStyles;
